import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getMessages, sendMessage } from '../../services/chatService';

const ROLE_LABELS = {
  admin: 'Admin',
  manager: 'Manager',
  associate: 'Associate',
  banker: 'Banker',
};

const ROLE_COLORS = {
  admin: '#E63757',
  manager: '#2C7BE5',
  associate: '#00D27A',
  banker: '#6B5CE7',
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMins / 60);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHrs < 24) return `${diffHrs}h ago`;

  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function FloatingChat() {
  const { fbUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const containerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const isInitialLoad = useRef(true);
  const pollRef = useRef(null);
  const latestCountRef = useRef(0);

  const scrollToBottom = useCallback((instant = false) => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: instant ? 'instant' : 'smooth',
      });
    }
  }, []);

  // Fetch initial messages when opened
  useEffect(() => {
    if (!open) return;
    const fetchInitial = async () => {
      try {
        setLoading(true);
        isInitialLoad.current = true;
        const { data } = await getMessages(fbUser, 1);
        const fetched = data.data?.messages || [];
        setMessages(fetched);
        setHasMore(fetched.length >= 50);
        setPage(1);
        latestCountRef.current = fetched.length;
      } catch (err) {
        console.error('Failed to fetch messages:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchInitial();
  }, [fbUser, open]);

  // Scroll to bottom after initial load
  useEffect(() => {
    if (!loading && messages.length > 0 && isInitialLoad.current) {
      isInitialLoad.current = false;
      requestAnimationFrame(() => scrollToBottom(true));
    }
  }, [loading, messages.length, scrollToBottom]);

  // Poll every 10s when open
  useEffect(() => {
    if (!open) return;
    const poll = async () => {
      try {
        const { data } = await getMessages(fbUser, 1);
        const fetched = data.data?.messages || [];
        if (fetched.length !== latestCountRef.current) {
          const hadMore = fetched.length > latestCountRef.current;
          setMessages(fetched);
          latestCountRef.current = fetched.length;
          if (hadMore) requestAnimationFrame(() => scrollToBottom(false));
        }
      } catch (err) { /* silent */ }
    };
    pollRef.current = setInterval(poll, 10000);
    return () => clearInterval(pollRef.current);
  }, [fbUser, open, scrollToBottom]);

  // Load older on scroll top
  const handleScroll = async () => {
    if (!containerRef.current || !hasMore || loadingMore) return;
    if (containerRef.current.scrollTop < 40) {
      try {
        setLoadingMore(true);
        const prevH = containerRef.current.scrollHeight;
        const nextPage = page + 1;
        const { data } = await getMessages(fbUser, nextPage);
        const fetched = data.data?.messages || [];
        if (fetched.length > 0) {
          setMessages((prev) => [...fetched, ...prev]);
          setPage(nextPage);
          setHasMore(fetched.length >= 50);
          requestAnimationFrame(() => {
            if (containerRef.current) {
              containerRef.current.scrollTop = containerRef.current.scrollHeight - prevH;
            }
          });
        } else {
          setHasMore(false);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingMore(false);
      }
    }
  };

  const handleSend = async () => {
    const trimmed = content.trim();
    if (!trimmed || sending) return;
    setSending(true);
    setContent('');
    try {
      const { data } = await sendMessage(fbUser, trimmed);
      const newMsg = data.data?.message;
      if (newMsg) {
        setMessages((prev) => {
          const exists = prev.some((m) => m._id === newMsg._id);
          return exists ? prev : [...prev, newMsg];
        });
        latestCountRef.current += 1;
        requestAnimationFrame(() => scrollToBottom(false));
      }
    } catch (err) {
      console.error(err);
      setContent(trimmed);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isOwn = (msg) => msg.senderType === 'student';

  return (
    <>
      {/* Chat panel */}
      {open && (
        <div
          className="fixed z-50 flex flex-col overflow-hidden bg-white shadow-2xl"
          style={{
            bottom: 24,
            right: 24,
            width: 'min(380px, calc(100vw - 48px))',
            height: 520,
            borderRadius: 16,
            animation: 'chatSlideUp 0.25s ease-out',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-2.5 flex-shrink-0"
            style={{ backgroundColor: '#2563eb', color: '#fff', minHeight: 48 }}
          >
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span className="text-sm font-semibold">GBL Tree Support</span>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div
            ref={containerRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto py-3"
            style={{ backgroundColor: '#f5f5f5' }}
          >
            {loadingMore && (
              <div className="flex justify-center py-2">
                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-6">
                <div className="w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center mb-3">
                  <svg className="w-6 h-6 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <p className="text-sm text-slate-500">No messages yet</p>
                <p className="text-xs text-slate-400 mt-0.5">Start the conversation</p>
              </div>
            ) : (
              messages.map((msg) => {
                const own = isOwn(msg);
                return (
                  <div
                    key={msg._id}
                    className={`flex mb-1.5 px-3 ${own ? 'justify-end' : 'justify-start'}`}
                    style={{ animation: 'msgSlideIn 0.2s ease-out' }}
                  >
                    <div style={{ maxWidth: '75%', minWidth: 80 }}>
                      {!own && (
                        <div className="flex items-center gap-1 mb-0.5">
                          <span className="text-[10px] font-semibold text-slate-700">
                            {msg.senderName}
                          </span>
                          {msg.senderRole && msg.senderRole !== 'student' && (
                            <span
                              className="text-[8px] font-bold text-white px-1 py-px rounded-full leading-none"
                              style={{
                                backgroundColor: ROLE_COLORS[msg.senderRole] || '#9e9e9e',
                                height: 14,
                                display: 'inline-flex',
                                alignItems: 'center',
                              }}
                            >
                              {ROLE_LABELS[msg.senderRole] || msg.senderRole}
                            </span>
                          )}
                        </div>
                      )}

                      <div
                        className="px-3 py-1.5 break-words"
                        style={{
                          backgroundColor: own ? '#2563eb' : '#fff',
                          color: own ? '#fff' : '#1e293b',
                          borderRadius: 12,
                          borderTopRightRadius: own ? 4 : 12,
                          borderTopLeftRadius: own ? 12 : 4,
                          boxShadow: own ? 'none' : '0 1px 2px rgba(0,0,0,0.06)',
                          whiteSpace: 'pre-wrap',
                          fontSize: '0.82rem',
                          lineHeight: 1.45,
                        }}
                      >
                        {msg.content}
                      </div>

                      <p
                        className="mt-0.5 px-0.5"
                        style={{ fontSize: '0.6rem', color: '#94a3b8', textAlign: own ? 'right' : 'left' }}
                      >
                        {formatTime(msg.createdAt)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="flex-shrink-0 px-3 py-2.5 bg-white border-t border-slate-200">
            <div className="flex items-end gap-2">
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type a message..."
                rows={1}
                className="flex-1 resize-none text-sm max-h-20 py-2 px-3 border border-slate-200 rounded-xl outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                style={{ minHeight: 38, fontSize: '0.85rem' }}
              />
              <button
                type="button"
                onClick={handleSend}
                disabled={!content.trim() || sending}
                className="h-[38px] w-[38px] flex-shrink-0 flex items-center justify-center rounded-xl text-white disabled:opacity-40 hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#2563eb' }}
              >
                {sending ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed z-50 flex items-center justify-center w-14 h-14 rounded-full text-white shadow-lg hover:shadow-xl transition-all hover:scale-105"
          style={{
            bottom: 24,
            right: 24,
            backgroundColor: '#2563eb',
            animation: 'chatSlideUp 0.3s ease-out',
          }}
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </button>
      )}

      {/* Animations */}
      <style>{`
        @keyframes chatSlideUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes msgSlideIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}
